/**
 * The 13 CBWEE_IDENTITY__ env vars surfaced on Screen 7 (Runtime Config).
 * Source of truth: docs/frontend/design.md Screen 7 + .env.example.
 * The console NEVER reads real values — `displayState` is a fixed front-end
 * label, not a probe result.
 */
export type EnvVarDisplayState = 'maintained-by-ops' | 'masked';

export interface EnvVarSpec {
  name: string;
  sensitive: boolean;
  description: string;
  displayState: EnvVarDisplayState;
}

export const RUNTIME_ENV_VARS: readonly EnvVarSpec[] = [
  {
    name: 'CBWEE_IDENTITY__BIND_ADDR',
    sensitive: false,
    description: '服务监听地址（如 0.0.0.0:8080）。',
    displayState: 'maintained-by-ops',
  },
  {
    name: 'CBWEE_IDENTITY__PUBLIC_BASE_URL',
    sensitive: false,
    description: '对外可访问的基础 URL，用于构建回调地址。',
    displayState: 'maintained-by-ops',
  },
  {
    name: 'CBWEE_IDENTITY__DATABASE_URL',
    sensitive: true,
    description: 'Postgres 连接串；含口令，控制台仅以 mask 形式展示。',
    displayState: 'masked',
  },
  {
    name: 'CBWEE_IDENTITY__SESSION_SECRET',
    sensitive: true,
    description: '会话与 OAuth secret HMAC 派生的密钥，必须 mask。',
    displayState: 'masked',
  },
  {
    name: 'CBWEE_IDENTITY__OIDC_ISSUER',
    sensitive: false,
    description: 'OIDC issuer URL，与 /healthz 暴露的 issuer 字段一致。',
    displayState: 'maintained-by-ops',
  },
  {
    name: 'CBWEE_IDENTITY__OIDC_SIGNING_KEY_PATH',
    sensitive: false,
    description: 'RS256 私钥 PEM 路径；缺省时 JWKS 为空。',
    displayState: 'maintained-by-ops',
  },
  {
    name: 'CBWEE_IDENTITY__FEISHU_APP_ID',
    sensitive: true,
    description: '飞书自建应用 App ID，控制台不展示明文。',
    displayState: 'masked',
  },
  {
    name: 'CBWEE_IDENTITY__FEISHU_APP_SECRET',
    sensitive: true,
    description: '飞书自建应用 App Secret，必须 mask。',
    displayState: 'masked',
  },
  {
    name: 'CBWEE_IDENTITY__FEISHU_REDIRECT_URI',
    sensitive: false,
    description: '飞书 OAuth 回调地址；dev 必须设为 http://localhost:5173/auth/feishu/callback。',
    displayState: 'maintained-by-ops',
  },
  {
    name: 'CBWEE_IDENTITY__BOOTSTRAP_ADMIN_MOBILES',
    sensitive: true,
    description: '逗号分隔的手机号白名单；首登命中即授予 platform_admin。控制台不展示。',
    displayState: 'masked',
  },
  {
    name: 'CBWEE_IDENTITY__BOOTSTRAP_ADMIN_UNION_IDS',
    sensitive: true,
    description: '逗号分隔的飞书 union_id 白名单；首登命中即授予 platform_admin。控制台不展示。',
    displayState: 'masked',
  },
  {
    name: 'CBWEE_IDENTITY__TEST_OIDC_CLIENT_SECRET',
    sensitive: true,
    description: '启动时 HMAC-SHA256 写入 oauth_clients.client_secret_hash 列；明文不入库不展示。',
    displayState: 'masked',
  },
  {
    name: 'CBWEE_IDENTITY__OIDC_TOKEN_TTL_SECONDS',
    sensitive: false,
    description: 'access_token / id_token 的 TTL（秒）。运维侧维护。',
    displayState: 'maintained-by-ops',
  },
] as const;
